import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { toImageLabel } from '../types/LabelingTypes';
import type { ToolImage, ImageLabel } from '../types/LabelingTypes';
import { loadAutoAdvance } from '../storage/labelingStorage';
import { sortByCapturedAt } from '../dichotomous/dichotomousSearch';
import { labelizeListSessionImages, labelizeSaveLabels , labelizeListPredictions, labelizeGetSignedImageUrl, labelizeDeleteLabel } from '../api/LabelingApiClient';
import { useApiClient } from '@/hooks/useApiClient';
import { LabelingContext, type LabelingContextType } from './LabelingContext';
import {
  INITIAL_STATE,
  computeFiltered,
  applyLabelAction,
  undoAction,
  bulkLabelAction,
  applyLabelsBulkAction,
  setAutoAdvanceAction,
  classifyWithAiAction,
  bulkClassifyWithAiAction,
} from './labelingActions';
import {
  rejectSuggestionReducer,
  rejectAllSuggestionsReducer,
  resetAnnotationsReducer,
  buildLabelsRecord,
  mapCnnLabel,
} from './labelingReducer';

// ---------- provider ----------

interface LabelingProviderProps {
  toolId: string;
  sessionId: string;
  children: React.ReactNode;
}

export function LabelingProvider({ toolId, sessionId, children }: LabelingProviderProps) {
  const http = useApiClient();

  const [items, setItems] = useState<ToolImage[]>(INITIAL_STATE.items);
  const [currentKey, setCurrentKey] = useState<string>(INITIAL_STATE.currentKey);
  const [mode, setModeState] = useState<'focus' | 'grid' | 'dichotomous'>(INITIAL_STATE.mode);
  const [statusFilter, setStatusFilterState] = useState<'all' | 'todo' | 'done'>(
    INITIAL_STATE.statusFilter
  );
  const [seriesFilter, setSeriesFilterState] = useState<string>(INITIAL_STATE.seriesFilter);
  const [selected, setSelected] = useState<Set<string>>(INITIAL_STATE.selected);
  // A plain UI preference — the only labeling value still read from localStorage. Restored
  // lazily so it is already correct on the first render, before any fetch resolves.
  const [autoAdvance, setAutoAdvanceState] = useState<boolean>(() => loadAutoAdvance());
  const [history, setHistory] = useState(INITIAL_STATE.history);

  // Keep refs for autoAdvance and mode so callbacks don't stale-close over them
  const autoAdvanceRef = useRef(autoAdvance);
  autoAdvanceRef.current = autoAdvance;
  const modeRef = useRef(mode);
  modeRef.current = mode;

  // Keep refs for active filters so auto-advance stays within the filtered set
  const statusFilterRef = useRef(statusFilter);
  statusFilterRef.current = statusFilter;
  const seriesFilterRef = useRef(seriesFilter);
  seriesFilterRef.current = seriesFilter;

  // Keep a ref for items to avoid reading state via nested setters
  const itemsRef = useRef(items);
  itemsRef.current = items;

  // ----- mount: fetch session images -----
  useEffect(() => {
    if (!http) return;
    const controller = new AbortController();

    labelizeListSessionImages(http, sessionId, controller.signal)
      .then((sessionImages) => {
        // Map LabelingSessionImage → ToolImage
        const fetched: ToolImage[] = sessionImages.map((img) => ({
          key: img.imageKey,
          experimentId: img.experimentId,
          partId: img.partId,
          operationId: img.operationId,
          toolId: img.partId ?? img.experimentId,
          ref: img.operationId ?? img.partId ?? img.experimentId,
          machine: img.operationId ?? '',
          magnification: 0,
          edge: 0,
          capturedAt: img.createdAt ?? new Date().toISOString(),
          imageUrl: img.url,
          label: toImageLabel(img.label),
          cnn: null,
        }));

        // Every view (grid, image list, part picker, navigation) follows this order: capture
        // time, so a part reads from its first to its last image whatever order the API used.
        const ordered = sortByCapturedAt(fetched);
        setItems(ordered);
        setCurrentKey(ordered[0]?.key ?? '');

        // Hydrate server-persisted CNN predictions as suggestions for items without one.
        // The guard skips items that already carry a suggestion, so a fresher one produced
        // in this session is never clobbered by the stored prediction.
        labelizeListPredictions(http, toolId, controller.signal)
          .then((stored) => {
            if (stored.length === 0) return;
            const byKey = new Map(stored.map((p) => [p.imageKey, p]));
            setItems((prev) =>
              prev.map((img) => {
                if (img.cnn) return img;
                const prediction = byKey.get(img.key);
                if (!prediction) return img;
                const label = prediction.predictedLabel === 'worn' ? 'worn' : ('good' as const);
                return {
                  ...img,
                  cnn: {
                    label,
                    mappedState: mapCnnLabel(label),
                    confidence: prediction.confidence,
                    at: prediction.updatedAt,
                  },
                };
              })
            );
          })
          .catch((err: unknown) => {
            if (err instanceof Error && err.name === 'AbortError') return;
            console.warn('[LabelingProvider] Stored predictions unavailable:', err);
          });
      })
      .catch((err: unknown) => {
        if (err instanceof Error && err.name === 'AbortError') return;
        console.error('[LabelingProvider] Failed to load session images:', err);
      });

    return () => {
      controller.abort();
    };
  }, [http, sessionId]);

  // ----- fire-and-forget label sync -----
  const saveLabelsToServer = useCallback(
    (nextItems: ToolImage[]) => {
      if (!http) return;
      const labels = buildLabelsRecord(nextItems);
      const flat: Record<string, ImageLabel | null> = Object.fromEntries(
        Object.entries(labels).map(([k, v]) => [k, v.label])
      );
      void labelizeSaveLabels(http, sessionId, flat);
    },
    [http, sessionId]
  );

  /**
   * An undo reverts exactly one image, possibly back to unset. `labelizeSaveLabels`
   * filters out null labels, so it structurally cannot express "clear this label" —
   * hence the delete route for that branch. Fire-and-forget like the other syncs in
   * this file: a failure is warned about, never retried nor rolled back.
   */
  const syncUndoToServer = useCallback(
    (key: string, label: ImageLabel | null) => {
      if (!http) return;
      const request =
        label === null
          ? labelizeDeleteLabel(http, sessionId, key)
          : labelizeSaveLabels(http, sessionId, { [key]: label });
      void request.catch((err: unknown) => {
        console.warn(`[LabelingProvider] Failed to sync the undo of ${key}:`, err);
      });
    },
    [http, sessionId]
  );

  // ----- derived values -----
  const filtered = useMemo(
    () => computeFiltered(items, statusFilter, seriesFilter),
    [items, statusFilter, seriesFilter]
  );

  const current = useMemo(
    () => items.find((i) => i.key === currentKey),
    [items, currentKey]
  );

  const stats = useMemo(() => {
    const ok = items.filter((i) => i.label === 'ok').length;
    const worn = items.filter((i) => i.label === 'worn').length;
    const unreadable = items.filter((i) => i.label === 'unreadable').length;
    const done = items.filter((i) => i.label !== null).length;
    return { ok, worn, unreadable, done, total: items.length };
  }, [items]);

  const progressPct = useMemo(
    () => (stats.total === 0 ? 0 : (stats.done / stats.total) * 100),
    [stats]
  );

  // ----- actions -----

  const jump = useCallback((key: string) => {
    setCurrentKey(key);
  }, []);

  const applyLabel = useCallback(
    (key: string, state: ImageLabel) => {
      applyLabelAction(
        key,
        state,
        setItems,
        setHistory,
        setCurrentKey,
        autoAdvanceRef,
        modeRef,
        statusFilterRef,
        seriesFilterRef,
        saveLabelsToServer
      );
    },
    [saveLabelsToServer]
  );

  const undo = useCallback(() => {
    undoAction(setHistory, setItems, setCurrentKey, syncUndoToServer);
  }, [syncUndoToServer]);

  const prev = useCallback(() => {
    setCurrentKey((prevKey) => {
      const idx = filtered.findIndex((i) => i.key === prevKey);
      if (idx <= 0) return prevKey;
      return filtered[idx - 1].key;
    });
  }, [filtered]);

  const next = useCallback(() => {
    setCurrentKey((prevKey) => {
      const idx = filtered.findIndex((i) => i.key === prevKey);
      if (idx === -1 || idx >= filtered.length - 1) return prevKey;
      return filtered[idx + 1].key;
    });
  }, [filtered]);

  const setMode = useCallback((m: 'focus' | 'grid' | 'dichotomous') => {
    setModeState(m);
    modeRef.current = m;
  }, []);

  const setStatusFilter = useCallback(
    (s: 'all' | 'todo' | 'done') => {
      setStatusFilterState(s);
      setCurrentKey((prevKey) => {
        const newFiltered = computeFiltered(items, s, seriesFilter);
        if (!newFiltered.find((i) => i.key === prevKey)) {
          return newFiltered[0]?.key ?? prevKey;
        }
        return prevKey;
      });
    },
    [items, seriesFilter]
  );

  const setSeriesFilter = useCallback(
    (v: string) => {
      setSeriesFilterState(v);
      seriesFilterRef.current = v;
      setCurrentKey((prevKey) => {
        const newFiltered = computeFiltered(items, statusFilter, v);
        if (!newFiltered.find((i) => i.key === prevKey)) {
          return newFiltered[0]?.key ?? prevKey;
        }
        return prevKey;
      });
    },
    [items, statusFilter]
  );

  const toggleSelect = useCallback((key: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const bulkLabel = useCallback(
    (state: ImageLabel) => {
      bulkLabelAction(state, setSelected, setItems, setHistory, saveLabelsToServer);
    },
    [saveLabelsToServer]
  );

  const applyLabelsBulk = useCallback(
    async (entries: Array<{ key: string; label: ImageLabel }>) => {
      applyLabelsBulkAction(entries, setItems, setHistory, saveLabelsToServer);
    },
    [saveLabelsToServer]
  );

  const clearSelection = useCallback(() => {
    setSelected(new Set());
  }, []);

  const setAutoAdvance = useCallback((b: boolean) => {
    setAutoAdvanceAction(b, setAutoAdvanceState, autoAdvanceRef);
  }, []);

  const classifyWithAi = useCallback(
    async (key: string) => {
      await classifyWithAiAction(key, setItems, http);
    },
    [http]
  );

  // Presigned URLs expire mid-session; the <img> onError handlers call this to re-sign
  // that one image. On failure the stale URL stays in place — no retry loop.
  const refreshImageUrl = useCallback(
    async (key: string) => {
      if (!http) return;
      const item = itemsRef.current.find((i) => i.key === key);
      if (!item || item.partId === null || item.operationId === null) return;
      try {
        const url = await labelizeGetSignedImageUrl(
          http,
          item.experimentId,
          item.partId,
          item.operationId,
          item.key
        );
        setItems((prev) => prev.map((i) => (i.key === key ? { ...i, imageUrl: url } : i)));
      } catch (err: unknown) {
        console.warn('[LabelingProvider] Failed to refresh image URL:', err);
      }
    },
    [http]
  );

  const acceptSuggestion = useCallback(
    (key: string) => {
      const item = items.find((i) => i.key === key);
      if (!item?.cnn) return;
      const state = item.cnn.mappedState;
      applyLabel(key, state);
      setItems((prev) => rejectSuggestionReducer(prev, key));
    },
    [items, applyLabel]
  );

  const rejectSuggestion = useCallback((key: string) => {
    setItems((prev) => rejectSuggestionReducer(prev, key));
  }, []);

  const rejectAllSuggestions = useCallback(() => {
    const keys = new Set(filtered.filter((i) => i.cnn !== null).map((i) => i.key));
    setItems((prev) => rejectAllSuggestionsReducer(prev, keys));
  }, [filtered]);

  const classifyAllWithAi = useCallback(async () => {
    await Promise.all(filtered.map((item) => classifyWithAiAction(item.key, setItems, http)));
  }, [filtered, http]);

  const acceptAllSuggestions = useCallback(() => {
    const pending = filtered.filter((i) => i.cnn !== null);
    pending.forEach((item) => {
      applyLabel(item.key, item.cnn!.mappedState);
      setItems((prev) => rejectSuggestionReducer(prev, item.key));
    });
  }, [filtered, applyLabel]);

  /**
   * DEV reset — clears the session's labels everywhere they live: the database, then local
   * state. The database is the only thing the mount effect reads, so deleting there is what
   * makes the reset survive a reload.
   *
   * There is no bulk-clear route, so this fans out one delete per labeled image (unlabeled
   * ones are skipped — the route 404s on them). `allSettled`, not `all`: one rejected delete
   * (a 404 racing another tab, say) must not abort the remaining ones and leave the session
   * half-reset; every failure is warned about instead.
   */
  const resetAnnotations = useCallback(async () => {
    const labeled = itemsRef.current.filter((item) => item.label !== null);
    if (http && labeled.length > 0) {
      const outcomes = await Promise.allSettled(
        labeled.map((item) => labelizeDeleteLabel(http, sessionId, item.key))
      );
      outcomes.forEach((outcome, index) => {
        if (outcome.status === 'rejected') {
          console.warn(
            `[LabelingProvider] Failed to delete the label of ${labeled[index].key}:`,
            outcome.reason
          );
        }
      });
    }
    setItems((prev) => resetAnnotationsReducer(prev));
    setHistory([]);
  }, [http, sessionId]);

  const bulkClassifyWithAi = useCallback(async () => {
    await bulkClassifyWithAiAction(selected, setItems, setSelected, http);
  }, [selected, http]);

  // ----- context value -----
  const value: LabelingContextType = {
    toolId,
    sessionId,
    items,
    currentKey,
    mode,
    statusFilter,
    seriesFilter,
    selected,
    autoAdvance,
    history,
    filtered,
    current,
    stats,
    progressPct,
    applyLabel,
    undo,
    jump,
    goPrev: prev,
    goNext: next,
    setMode,
    setStatusFilter,
    setSeriesFilter,
    toggleSelect,
    bulkLabel,
    applyLabelsBulk,
    clearSelection,
    setAutoAdvance,
    classifyWithAi,
    refreshImageUrl,
    acceptSuggestion,
    rejectSuggestion,
    acceptAllSuggestions,
    rejectAllSuggestions,
    classifyAllWithAi,
    resetAnnotations,
    bulkClassifyWithAi,
  };

  return <LabelingContext.Provider value={value}>{children}</LabelingContext.Provider>;
}
